import React, { useEffect, useState } from 'react';

import './Integrations.scss';
import { Avatar, Button, Card, Col, Modal, Row, Tooltip } from 'antd';
import { ReactSVG } from 'react-svg';
import { SetupSendgrid } from './Integrations/Sendgrid';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

import { doGetSettings } from '../../../modules/settings/store/thunk';
import { clearState, selectAllSettings } from '../../../modules/settings/store/slice';
import { SetupShopify } from './Integrations/Shopify';
import { CheckCircleFilled, PauseCircleFilled } from '@ant-design/icons';
import { ROLES } from '../../../constants';
import { SetupSendcloud } from './Integrations/Sendcloud';
import { SetupWoocommerce } from './Integrations/Woocommerce';
import { SetupDeliveryMatch } from './Integrations/DeliveryMatch';
const { Meta } = Card;

const Integrations = () => {
  const dispatch = useDispatch();
  // dummy state for sendcloud & delivery match
  const [sendcloud, setSendcloud] = useState({});
  const [deliveryMatch, setDeliveryMatch] = useState({});
  const [modal, setModal] = useState({ isOpen: false, title: '', content: null });
  const resetModal = (options) => {
    if (options?.API_PUBLIC_KEY) {
      setSendcloud({ API_PUBLIC_KEY: options?.API_PUBLIC_KEY, SECRET_KEY: options?.SECRET_KEY });
    }
    if (options?.API_KEY) {
      setDeliveryMatch({ API_KEY: options?.API_KEY, CLIENT: options?.CLIENT });
    }
    setModal({ isOpen: false, title: '', content: null });

    if (options?.refetch && role === ROLES.ADMIN) {
      // refetch state from backend
      dispatch(doGetSettings({ category: 'integrations' }));
    }
  };

  const role = useSelector((state) => state.auth.user.role);

  const isFetching = useSelector((state) => state.setting.isFetching);
  const settings = useSelector((state) => selectAllSettings(state));

  const sendgridSettings = settings.filter((setting) => setting.group === 'SENDGRID');

  useEffect(() => {
    if (role === ROLES.ADMIN) {
      dispatch(doGetSettings({ category: 'integrations' }));
    }
    return () => dispatch(clearState()); // explicitly clear the state to prevent it from being used in different component
  }, []);

  const getSendgridStatusText = (settings) =>
    settings.length >= 2 ? 'Sendgrid is turned on' : settings.length === 1 ? 'Sendgrid is partially configured' : '';

  const getSendgridStatusColor = (settings) =>
    settings.length >= 2 ? 'limeGreen' : settings.length === 1 ? 'orange' : '';

  const SendgridTitle = () => {
    if (sendgridSettings.length === 0) {
      return 'Sendgrid';
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Sendgrid</span>
        {sendgridSettings.length === 1 ? (
          <PauseCircleFilled style={{ color: getSendgridStatusColor(sendgridSettings) }} />
        ) : (
          <CheckCircleFilled style={{ color: getSendgridStatusColor(sendgridSettings) }} />
        )}
      </div>
    );
  };

  return (
    <div className="info">
      <div className="info-content">
        <h3>Integrations</h3>
        <h5>Find and enable new integrations to optimize your delivery operations</h5>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <Button
          className="CreateButton ButtonWhite"
          size="small"
          onClick={() => window.open('mailto:maurice.stam@sparqle.com?subject=Integration Request')}
        >
          Request integration
          <ReactSVG className="icon" src="/icons/plus.svg" />
        </Button>
      </div>
      <Row gutter={[16, 16]}>
        {[ROLES.ADMIN, ROLES.COMPANY_OWNER].includes(role) && (
          <>
            <Col span={8}>
              <Card>
                <Meta
                  avatar={<Avatar src="/icons/woocommerce_icon.png" />}
                  title="Woocommerce"
                  description="If you have a Woocommerce storefront you can integrate easily and delivery your orders directly via Sparqle."
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() =>
                      setModal({
                        isOpen: true,
                        title: 'Woocommerce Integration',
                        content: <SetupWoocommerce isFetching={isFetching} done={resetModal} />,
                      })
                    }
                  >
                    Setup integration
                  </Button>
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Meta
                  avatar={<Avatar src="/icons/shopify.png" />}
                  title="Shopify"
                  description="If you have a Shopify storefront you can integrate easily and delivery your orders directly via Sparqle."
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() =>
                      setModal({
                        isOpen: true,
                        title: 'Shopify Integration',
                        content: <SetupShopify isFetching={isFetching} done={resetModal} />,
                      })
                    }
                  >
                    Setup integration
                  </Button>
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Meta
                  avatar={<Avatar src="/icons/sendcloud.png" />}
                  title="Sendcloud"
                  description="If you have a Sendcloud account you can integrate easily and deliver your orders directly via Sparqle."
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() =>
                      setModal({
                        isOpen: true,
                        title: 'Sendcloud',
                        content: <SetupSendcloud isFetching={isFetching} done={resetModal} existing={sendcloud} />,
                      })
                    }
                  >
                    Setup integration
                  </Button>
                </div>
              </Card>
            </Col>
          </>
        )}
        {[ROLES.ADMIN].includes(role) && (
          <>
            <Col span={8}>
              <Card style={{ height: '100%' }}>
                <Meta
                  avatar={<Avatar src="/icons/moneybird.png" />}
                  title="Moneybird"
                  description="Moneybird offers entrepreneurs an online accounting tool for managing their finances."
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <Button size="small" type="link">
                    Contact Us
                  </Button>
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card style={{ height: '100%' }}>
                <Tooltip title={getSendgridStatusText(sendgridSettings)}>
                  <Meta
                    avatar={<Avatar src="/icons/sendgrid.png" />}
                    title={<SendgridTitle />}
                    description="Sendgrid allows to configure emails to be sent to recipients on certain events."
                  />
                </Tooltip>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() =>
                      setModal({
                        isOpen: true,
                        title: 'Sendgrid Integration',
                        content: (
                          <SetupSendgrid
                            isFetching={isFetching}
                            done={resetModal}
                            existing={settings.filter((setting) => setting.group === 'SENDGRID')}
                          />
                        ),
                      })
                    }
                  >
                    {sendgridSettings.length ? 'Update integration' : 'Setup integration'}
                  </Button>
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card style={{ height: '100%' }}>
                <Meta
                  avatar={<Avatar src="/icons/deliverymatch.png" />}
                  title="Delivery Match"
                  description="Delivery Match is a logistics shipping management platform."
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() =>
                      setModal({
                        isOpen: true,
                        title: 'Delivery Match',
                        content: (
                          <SetupDeliveryMatch isFetching={isFetching} done={resetModal} existing={deliveryMatch} />
                        ),
                      })
                    }
                  >
                    Setup integration
                  </Button>
                </div>
              </Card>
            </Col>
            {/* <Col span={8}>
              <Card style={{ height: '100%' }}>
                <Meta
                  avatar={<Avatar src="/icons/veloyd.png" />}
                  title="Veloyd"
                  description="Veloyd is a delivery software platform. Connect your account to sync your orders."
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() =>
                      setModal({
                        isOpen: true,
                        title: 'Veloyd Integration',
                        content: <SetupVeloyd isFetching={isFetching} done={resetModal} existing={veloydSettings} />,
                      })
                    }
                  >
                    {veloydSettings.length ? 'Update integration' : 'Setup integration'}
                  </Button>
                </div>
              </Card>
            </Col> */}
          </>
        )}
        {/* <Col span={8}>
          <Card>
            <Meta
              avatar={<Avatar src="/icons/woocommerce.png" />}
              title="WooCommerce"
              description="Veloyd is like the ali-express version of Sparqle. Connect your account to see how bad it actually is."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <Button size="small" type="link">
                Contact Support
              </Button>
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Meta
              avatar={<Avatar src="/icons/lightspeed.jpg" />}
              title="Lightspeed"
              description="Veloyd is like the ali-express version of Sparqle. Connect your account to see how bad it actually is."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <Button size="small" type="link">
                Contact Support
              </Button>
            </div>
          </Card>
        </Col> */}
      </Row>
      <Modal
        width={[/Shopify/, /Woocommerce/].some((r) => r.test(modal.title)) ? 800 : 552}
        closeIcon={false}
        title={modal.title}
        open={modal.isOpen}
        forceRender
        onCancel={resetModal}
      >
        {modal.content}
      </Modal>
    </div>
  );
};

export default Integrations;
